/**
 * Product-related API endpoints
 */
import { apiGet } from "./client.js";

const DEFAULT_COLLECTION_PAGE_SIZE = 100;

const getStrapiDataAndPagination = (json) => {
  const data = json?.data?.data || json?.data || json || [];
  const pagination = json?.data?.meta?.pagination || json?.meta?.pagination || null;

  return { data: Array.isArray(data) ? data : [], pagination };
};

const apiGetAllPages = async (endpoint, params = {}) => {
  const pageSize = Number(params.pageSize) > 0 ? Number(params.pageSize) : DEFAULT_COLLECTION_PAGE_SIZE;
  let page = Number(params.page) > 0 ? Number(params.page) : 1;
  let pageCount = page;
  const results = [];

  do {
    const response = await apiGet(endpoint, { ...params, page, pageSize });

    if (!response.ok) {
      const error = new Error(`HTTP ${response.status}`);
      error.status = response.status;
      error.response = response;
      throw error;
    }

    const json = await response.json();
    const { data, pagination } = getStrapiDataAndPagination(json);
    results.push(...data);

    pageCount = pagination?.pageCount || page;
    page += 1;
  } while (page <= pageCount);

  return results;
};

/**
 * Fetches a single product by slug and type
 * @param {string} slug - Product slug
 * @param {string} type - Product type (frames, sunglasses, accessories)
 * @returns {Promise<Response>} Product response
 */
export const getProduct = (slug, type = "frames") => {
  const endpoint = type === "sunglasses" ? "sunglasses" : type === "accessories" ? "accessories" : "frames";
  return apiGet(`/shop/${endpoint}/${slug}`);
};

/**
 * Fetches all frames with optional parameters
 * @param {Object} params - Query parameters
 * @returns {Promise<Response>} All frames response
 */
export const getAllFrames = (params = {}) => apiGet("/shop/frames", params);

/**
 * Fetches frames grouped by collection divisions
 * @param {Object} params - Optional query parameters
 * @returns {Promise<Response>} Frame collections response
 */
export const getFrameCollections = (params = {}) => apiGet("/shop/frames", { ...params, groupByCollection: true });

/**
 * Fetches a single frame collection and its frames by collection slug
 * @param {string} collectionSlug - Frame collection slug
 * @returns {Promise<Response>} Frame collection response
 */
export const getFrameCollection = (collectionSlug) => apiGet(`/shop/frames/collections/${collectionSlug}`);

/**
 * Fetches a single frame by slug
 * @param {string} slug - Frame slug
 * @returns {Promise<Response>} Frame response
 */
export const getFrame = (slug) => apiGet(`/shop/frames/${slug}`);

/**
 * Fetches a single frame by document ID
 * @param {string} id - Frame document ID
 * @param {Object} params - Query parameters
 * @returns {Promise<Response>} Frame response
 */
export const getFrameById = (id, params = {}) => apiGet(`/shop/frames/${id}`, { populate: "*", ...params });

/**
 * Fetches all sunglasses with optional parameters
 * @param {Object} params - Query parameters
 * @param {Object} options - Fetch options
 * @param {boolean} options.allPages - Fetch every Strapi page and return a combined array
 * @returns {Promise<Response|Array>} All sunglasses response or combined data when allPages is enabled
 */
export const getAllSunglasses = (params = {}, options = {}) =>
  options.allPages ? apiGetAllPages("/shop/sunglasses", params) : apiGet("/shop/sunglasses", params);

/**
 * Fetches a single pair of sunglasses by slug
 * @param {string} slug - Sunglasses slug
 * @returns {Promise<Response>} Sunglasses response
 */
export const getSunglasses = (slug) => apiGet(`/shop/sunglasses/${slug}`);

/**
 * Fetches a single pair of sunglasses by document ID
 * @param {string} id - Sunglasses document ID
 * @param {Object} params - Query parameters
 * @returns {Promise<Response>} Sunglasses response
 */
export const getSunglassesById = (id, params = {}) => apiGet(`/shop/sunglasses/${id}`, { populate: "*", ...params });

/**
 * Fetches all accessories with optional parameters
 * @param {Object} params - Query parameters
 * @param {Object} options - Fetch options
 * @param {boolean} options.allPages - Fetch every Strapi page and return a combined array
 * @returns {Promise<Response|Array>} All accessories response or combined data when allPages is enabled
 */
export const getAllAccessories = (params = {}, options = {}) =>
  options.allPages ? apiGetAllPages("/shop/accessories", params) : apiGet("/shop/accessories", params);

/**
 * Fetches a single accessory by slug
 * @param {string} slug - Accessory slug
 * @returns {Promise<Response>} Accessory response
 */
export const getAccessory = (slug) => apiGet(`/shop/accessories/${slug}`);

/**
 * Fetches a single accessory by document ID
 * @param {string} id - Accessory document ID
 * @param {Object} params - Query parameters
 * @returns {Promise<Response>} Accessory response
 */
export const getAccessoryById = (id, params = {}) => apiGet(`/shop/accessories/${id}`, { populate: "*", ...params });

/**
 * Fetches lens types
 * @returns {Promise<Response>} Lens types response
 */
export const getLensTypes = () => apiGet("/shop/lens-types", { populate: "*" });

/**
 * Fetches all lenses with optional parameters
 * @param {Object} params - Query parameters
 * @returns {Promise<Response>} All lenses response
 */
export const getAllLenses = (params = {}) => apiGet("/shop/lenses", params);

/**
 * Fetches lenses by type ID
 * @param {string} typeId - Lens type ID
 * @returns {Promise<Response>} Lenses response
 */
export const getLensesByType = (typeId) => apiGet("/shop/lenses", { type: typeId, populate: "type" });

/**
 * Fetches a single lens by document ID
 * @param {string} id - Lens document ID
 * @param {Object} params - Query parameters
 * @returns {Promise<Response>} Lens response
 */
export const getLensById = (id, params = {}) => apiGet(`/shop/lenses/${id}`, { populate: "*", ...params });

/**
 * Determines product category from endpoint
 * @param {string} endpoint - API endpoint or URL path
 * @returns {string} Product category (frames, sunglasses, accessories, lenses)
 */
export const getProductCategory = (endpoint) => {
  if (!endpoint || !endpoint.includes("/product/")) {
    return "frames";
  }

  return endpoint.includes("/product/sunglasses/")
    ? "sunglasses"
    : endpoint.includes("/product/accessories/")
      ? "accessories"
      : endpoint.includes("/product/lenses/")
        ? "lenses"
        : endpoint.includes("/product/frames/")
          ? "frames"
          : "frames";
};
